/** The Import Runs search namespaces wired to their suggestion fetchers.
 *
 * Two families (see ImportRunFilterNode in the OpenAPI spec):
 *
 * - Direct Import_Run columns: id/group/request/task (exact), status/origin
 *   (label or raw integer), tenant, created (whole-day or datetime range).
 * - Import_Run_Index-backed record references (booking, order, user, tour,
 *   activity, itinerary, departure, date, source, supplier, reseller,
 *   participant, event + the `*_id` variants): EXACT value matches only —
 *   that is what the index supports. Child-run index rows are mirrored onto
 *   the group parent, so matching a child surfaces its parent row.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * (contract-tested). Exact-only namespaces round-trip fine through the `=`
 * sigil (`=` resolves to `exact` when `iexact` is absent — same rule as the
 * EDL tour/participant namespaces).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/importRuns';

import { IMPORT_ORIGIN_LABELS, IMPORT_STATUS_LABELS } from './importRunFormat';

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

/** Exact-match namespace over one Import_Run_Index type. */
function indexRef(label: string, help: string, examples: string[]) {
  return {
    label,
    operators: ['exact' as const],
    valueType: 'string' as const,
    help: `${help} Exact match (the import search index stores exact values).`,
    examples,
  };
}

export function buildImportRunNamespaces(): NamespaceConfigMap {
  return {
    status: {
      label: 'status',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(IMPORT_STATUS_LABELS),
      help: 'Import run status, by label or raw integer value.',
      examples: ['status="import failure"', 'status=4'],
    },
    origin: {
      label: 'origin',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(IMPORT_ORIGIN_LABELS),
      help: 'What triggered the import, by label or raw integer value.',
      examples: ['origin=integration', 'origin=dashboard'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code; an all-digits value also matches the tenant id.',
      examples: ['tenant=curatedplanet', 'tenant=42'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the import started, UTC. `=` matches a whole day; a range takes date-time bounds. Keep a bounded window for fast searches.',
      examples: ['created=2026-07-20', 'created="2026-07-14 09:00".."2026-07-21 18:00"'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'string',
      help: 'Import run UUID (exact).',
      examples: ['id=0d9f1c2e-6f3a-4b8e-9c1d-2a3b4c5d6e7f'],
    },
    group: {
      label: 'group',
      operators: ['exact'],
      valueType: 'string',
      help: 'Group UUID — the parent run of a preprocessed/parallel import (exact).',
      examples: ['group=0d9f1c2e-6f3a-4b8e-9c1d-2a3b4c5d6e7f'],
    },
    request: {
      label: 'request',
      operators: ['exact'],
      valueType: 'string',
      help: 'HTTP request id that created the run — matches the run itself and, for group members, the parent (exact).',
      examples: ['request=abcd1234efgh'],
    },
    task: {
      label: 'task',
      operators: ['exact'],
      valueType: 'string',
      help: 'Celery task id of the processing task (exact).',
      examples: ['task=8f14e45f-ceea-4672-a2e5-6f3a4b8e9c1d'],
    },
    booking: indexRef('booking', "Booking ID affected by the import (custom id, or the booking's alt id).", ['booking=BK-1001']),
    order: indexRef('order', 'Order reference affected by the import.', ['order=ORD-100']),
    supplier: indexRef('supplier', 'Supplier reference affected by the import.', ['supplier=SUP-1']),
    reseller: indexRef('reseller', 'Reseller reference affected by the import.', ['reseller=RSL-1']),
    user: indexRef('user', 'Guest/user (usually the email) affected by the import.', ['user=jane@example.com']),
    user_id: indexRef('user_id', 'Internal user id affected by the import.', ['user_id=12345']),
    participant: indexRef('participant', 'Tour participant reference affected by the import.', ['participant=88123']),
    tour: indexRef('tour', 'Tour (external id/name as indexed) affected by the import.', ['tour=summer-tour-1']),
    tour_id: indexRef('tour_id', 'Internal tour id affected by the import.', ['tour_id=2041']),
    itinerary: indexRef('itinerary', 'Itinerary (external id/name as indexed) affected by the import.', ['itinerary=grand-circle']),
    itinerary_id: indexRef('itinerary_id', 'Internal itinerary id affected by the import.', ['itinerary_id=311']),
    activity: indexRef('activity', 'Activity (external id/name as indexed) affected by the import.', ['activity=museum-visit']),
    activity_id: indexRef('activity_id', 'Internal activity id affected by the import.', ['activity_id=77']),
    departure: indexRef('departure', 'Departure affected by the import.', ['departure=2026-08-01 09:30']),
    date: {
      ...indexRef('date', 'Trip/departure date affected by the import.', ['date=2026-08-01']),
      valueType: 'date',
    },
    source: indexRef('source', 'Import source code the run processed.', ['source=bokun']),
    event: indexRef('event', 'Event reference recorded by the import.', ['event=booking.created']),
  };
}
